import {
    ChangeEvent,
    ForwardedRef,
    PropsWithChildren,
    createContext,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { InputFormProps, InputRootProps } from '@givebutter/primitives/input';
import { bindForwardedRef } from '@givebutter/react-util';
import { useFormError } from 'components/form/UseFormError';
import * as Input from 'components/input';

interface IUseFormTextInputContext<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K> {}

const UseFormTextInputContext = createContext<IUseFormTextInputContext<any, any>>({
    name: '',
});

interface UseFormTextInputRootProps<T extends FieldValues, K extends FieldPath<T>>
    extends IUseFormTextInputContext<T, K>,
        PropsWithChildren {
    inputRootProps?: Omit<InputRootProps, 'children'>;
}

const UseFormTextInputRoot = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormTextInputRootProps<T, K>,
) => {
    const { name, children, inputRootProps = {}, disabled, ...restRootProps } = props;
    const error = useFormError(name);

    const ctx = useMemo(
        () => ({
            name,
            disabled,
            ...restRootProps,
        }),
        [disabled, name, restRootProps],
    );

    return (
        <UseFormTextInputContext.Provider value={ctx}>
            <Input.Root isError={!!error?.message} disabled={disabled} {...inputRootProps}>
                {children}
            </Input.Root>
        </UseFormTextInputContext.Provider>
    );
};

const UseFormTextInputForm = forwardRef(
    (props: InputFormProps, ref: ForwardedRef<HTMLInputElement>) => {
        const { onChange: onChangeConsumer, ...restConsumerProps } = props;
        const controllerProps = useContext(UseFormTextInputContext);

        const {
            field: { onChange: onChangeInternal, value, ref: formRef, ...restFormProps },
        } = useController(controllerProps);

        return (
            <Input.Form
                ref={node => {
                    formRef(node);
                    bindForwardedRef(ref, node);
                }}
                value={value}
                onChange={undefined}
                onInput={e => {
                    onChangeInternal(e);
                    onChangeConsumer?.(e as ChangeEvent<HTMLInputElement>);
                }}
                {...restFormProps}
                {...restConsumerProps}
            />
        );
    },
);

export { UseFormTextInputForm, UseFormTextInputRoot };
export type { UseFormTextInputRootProps };
